import { useState, useContext } from 'react';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import { MoreVertical, Users, MessageCircle, Calendar, Edit, Trash2, ArrowRight, Clock } from 'lucide-react';
import { ChainlitContext, IOperation, operationsDialogViewState } from '@chainlit/react-client';
import { operationsService } from '@/services/operationsService';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface OperationCardProps {
  operation: IOperation;
  onEdit?: (operationId: string) => void;
  onDelete?: (operationId: string) => void;
  viewMode?: 'grid' | 'list';
}

/**
 * Displays an operation as a card with metadata and action buttons.
 * Supports both grid and list view modes.
 */
export default function OperationCard({ operation, onEdit, onDelete, viewMode = 'grid' }: OperationCardProps) {
  const apiClient = useContext(ChainlitContext);
  const setDialogView = useSetRecoilState(operationsDialogViewState);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Switches the dialog to show the detail view for this operation.
   */
  const handleView = () => {
    setDialogView({ view: 'detail', operationId: operation.id });
  };

  /**
   * Triggers the edit callback for this operation.
   */
  const handleEdit = () => {
    onEdit?.(operation.id);
  };

  /**
   * Deletes the operation after user confirmation.
   */
  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete "${operation.name}"?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await operationsService.deleteOperation(apiClient, operation.id);
      onDelete?.(operation.id);
      toast.success('Operation deleted successfully');
    } catch (error) {
      toast.error('Failed to delete operation');
      console.error('Delete operation error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Formats a date string to a short readable format.
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  /**
   * Formats a date string to relative time (e.g., "2h ago", "3d ago").
   */
  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return formatDate(dateString);
  };

  if (viewMode === 'list') {
    return (
      <Card className="group hover:shadow-lg hover:shadow-primary/5 transition-all duration-200 cursor-pointer border-2 hover:border-primary/20" onClick={handleView}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 shadow-sm"
                style={{ backgroundColor: operation.color }}
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg line-clamp-1 mb-1">
                  {operation.name}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {operation.description || 'No description provided'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <MessageCircle className="h-4 w-4" />
                  <span className="font-medium">{operation.threadIds.length}</span>
                  <span className="text-xs">threads</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{formatRelativeTime(operation.createdAt)}</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 max-lg:h-9 max-lg:w-9 max-lg:opacity-100"
                      disabled={isDeleting}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleView(); }}>
                      <Users className="mr-2 h-4 w-4" />
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(); }}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Operation
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                      className="text-destructive focus:text-destructive"
                      disabled={isDeleting}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="group hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 cursor-pointer border-2 hover:border-primary/30 hover:-translate-y-1" onClick={handleView}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div
              className="w-6 h-6 rounded-full flex-shrink-0 shadow-md ring-2 ring-background"
              style={{ backgroundColor: operation.color }}
            />
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-lg line-clamp-1 mb-1 group-hover:text-primary transition-colors">
                {operation.name}
              </h3>
              <div className="flex items-center space-x-2">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {formatRelativeTime(operation.createdAt)}
                </span>
              </div>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-all duration-200 h-8 w-8 p-0 hover:bg-primary/10 max-lg:h-9 max-lg:w-9 max-lg:opacity-100"
                disabled={isDeleting}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleView(); }}>
                <Users className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(); }}>
                <Edit className="mr-2 h-4 w-4" />
                Edit Operation
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                className="text-destructive focus:text-destructive"
                disabled={isDeleting}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
          {operation.description || 'No description provided'}
        </p>
        
        <Separator className="my-3" />
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 bg-muted/50 px-3 py-1.5 rounded-full">
              <MessageCircle className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{operation.threadIds.length}</span>
              <span className="text-xs text-muted-foreground">threads</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

